import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, InputNumber, Select, message, Tag, Drawer, Modal, Tooltip, Popconfirm, Timeline, Typography, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, PercentageOutlined, EyeOutlined, HistoryOutlined,
  CheckCircleOutlined, DashboardOutlined, ArrowUpOutlined, SearchOutlined, ClockCircleOutlined,
} from '@ant-design/icons'
import { useMasterData, type Tariff } from '@/context/MasterDataContext'
import { useModulePermission } from '@/hooks/useModulePermission'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'
import { ApiError } from '@/services/api'

const { Text } = Typography
const BASE = '/master/tariffs'
const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const TariffMaster: React.FC = () => {
  const { tariffs, deleteTariff, saveTariff, masterDataLoaded } = useMasterData()
  const { canAdd, canEdit, canDelete } = useModulePermission('Masters')
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)

  const [viewRecord, setViewRecord] = useState<Tariff | null>(null)
  const [reviseModalRecord, setReviseModalRecord] = useState<Tariff | null>(null)
  const [form] = Form.useForm()
  const [reviseForm] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [savingRevision, setSavingRevision] = useState(false)

  const activeTariffs = tariffs.filter(t => t.status === 'active')
  const activeCount = activeTariffs.length
  const activePfTariffs = activeTariffs.filter(t => t.powerFacilitationCharge)
  const avgPf = activePfTariffs.reduce((s, t) => s + Number(t.powerFacilitationCharge), 0) / (activePfTariffs.length || 1)
  const maxPf = activePfTariffs.length ? Math.max(...activePfTariffs.map(t => Number(t.powerFacilitationCharge))) : 0

  const filteredTariffs = tariffs.filter(t => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      (t.tariffCode || '').toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || t.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      form.resetFields()
      form.setFieldsValue({
        effectiveFrom: new Date().toISOString().slice(0, 10),
        status: 'active'
      })
    } else if (mode === 'edit') {
      const rec = tariffs.find(t => t.id === editId)
      if (rec) {
        form.setFieldsValue({
          ...rec,
          powerFacilitationCharge: rec.powerFacilitationCharge ? Number(rec.powerFacilitationCharge) * 100 : undefined
        })
      } else goList()
    }
  }, [mode, editId, tariffs])

  const openReviseModal = (t: Tariff) => {
    setReviseModalRecord(t)
    const pfPaise = t.powerFacilitationCharge ? Number(t.powerFacilitationCharge) * 100 : 0
    reviseForm.setFieldsValue({
      tariffCode: t.tariffCode,
      description: t.description,
      currentPfCharge: pfPaise,
      newPfCharge: pfPaise,
      effectiveFrom: new Date().toISOString().slice(0, 10),
      effectiveTo: undefined,
      notes: `Revised tariff rates effective from ${new Date().toISOString().slice(0, 10)}`
    })
  }

  const handleSaveRevision = async () => {
    try {
      const values = await reviseForm.validateFields()
      if (!reviseModalRecord) return

      setSavingRevision(true)
      const newPfCharge = values.newPfCharge ? Number(values.newPfCharge) / 100 : 0
      const effectiveFrom = values.effectiveFrom || new Date().toISOString().slice(0, 10)
      const yesterday = new Date(new Date(effectiveFrom).getTime() - 86400000).toISOString().slice(0, 10)

      // 1. Mark existing active record as superseded
      await saveTariff({
        ...reviseModalRecord,
        status: 'superseded',
        effectiveTo: yesterday,
      }, true)

      // 2. Build history entry
      const existingHistory = (reviseModalRecord as any).revisionHistory || []
      const historyItem = {
        id: genId('TRV'),
        tariffCode: reviseModalRecord.tariffCode,
        powerFacilitationCharge: newPfCharge,
        effectiveFrom,
        effectiveTo: values.effectiveTo || undefined,
        description: values.description || reviseModalRecord.description,
        revisedAt: new Date().toISOString(),
        notes: values.notes || 'Tariff rate revision',
      }

      // 3. Save new active tariff version
      const newRecord: Tariff = {
        id: genId('T'),
        tariffCode: reviseModalRecord.tariffCode,
        description: values.description || reviseModalRecord.description,
        powerFacilitationCharge: newPfCharge,
        applicableTo: 'both',
        effectiveFrom,
        effectiveTo: values.effectiveTo || undefined,
        status: 'active',
        revisionHistory: [historyItem, ...existingHistory],
      } as any

      await saveTariff(newRecord, false)
      message.success(`Tariff ${reviseModalRecord.tariffCode} revised successfully!`)
      setReviseModalRecord(null)
      reviseForm.resetFields()
    } catch (err) {
      console.error('Revision Error:', err)
    } finally {
      setSavingRevision(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteTariff(id)
      message.success('Tariff removed successfully')
      if (isForm) goList()
    } catch { /* error handled by context */ }
  }

  const onFinish = async (values: any) => {
    let tariffCode = values.tariffCode
    if (mode === 'new') {
      try {
        tariffCode = await generateCode('tariffs')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate tariff code.")
        return
      }
    }

    const finalValues = {
      ...values,
      tariffCode,
      applicableTo: 'both',
      powerFacilitationCharge: values.powerFacilitationCharge ? Number(values.powerFacilitationCharge) / 100 : undefined,
    }

    setSubmitting(true)
    try {
      if (mode === 'edit') {
        await saveTariff({ ...finalValues, id: editId }, true)
        message.success('Tariff updated successfully')
      } else {
        await saveTariff({ ...finalValues, id: genId('T') }, false)
        message.success('Tariff added successfully')
      }
      goList()
    } catch { /* error handled by context */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Tariff Code', dataIndex: 'tariffCode', width: 130,
      render: (v: string) => (
        <span style={{ ...MONO, fontWeight: 700, color: '#1677ff', fontSize: 13 }}>
          {v}
        </span>
      ),
    },
    {
      title: 'Description', dataIndex: 'description',
      render: (v: string) => <span style={{ fontWeight: 500, color: '#0B1426' }}>{v}</span>,
    },
    {
      title: 'PF Charge (Paise/unit)', dataIndex: 'powerFacilitationCharge', align: 'right' as const,
      render: (v: number | string | undefined) => (
        v != null ? <span style={{ ...MONO, fontWeight: 700, fontSize: 15, color: '#059669' }}>{(Number(v) * 100).toFixed(0)} Paise</span> : <span style={{ color: '#ccc' }}>—</span>
      ),
    },
    {
      title: 'Effective From', dataIndex: 'effectiveFrom', width: 140,
      render: (v: string) => <span style={{ ...MONO, fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Effective To', dataIndex: 'effectiveTo', width: 140,
      render: (v: string) => (
        v ? <span style={{ ...MONO, fontSize: 12 }}>{v}</span> : <Tag color="blue" style={{ borderRadius: 100, fontSize: 11 }}>Current</Tag>
      ),
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      filters: [{ text: 'Active', value: 'active' }, { text: 'Superseded', value: 'superseded' }],
      onFilter: (value: any, record: Tariff) => record.status === value,
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Superseded'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_: any, record: Tariff) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View Details & History">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>

          {canEdit && (
            <Tooltip title="Edit Tariff">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
            </Tooltip>
          )}

          {canEdit && record.status === 'active' && (
            <Tooltip title="Revise Tariff Rate">
              <Button
                type="link"
                size="small"
                icon={<HistoryOutlined style={{ color: '#D97706' }} />}
                onClick={() => openReviseModal(record)}
                style={{ color: '#D97706', fontWeight: 600 }}
              >
                Revise
              </Button>
            </Tooltip>
          )}

          {canDelete && (
            <Popconfirm
              title="Delete this tariff record?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete Tariff">
                <Button type="link" danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit Form ──────────────────────────────────
  if (isForm) {
    return (
      <MasterFormShell
        title={mode === 'edit' ? 'Edit Sakthi Power Tariff' : 'Add Sakthi Power Tariff'}
        subtitle="Sakthi Power commercial tariff — facilitation charges and effective validity periods"
        onBack={goList}
        extra={mode === 'edit' && editId && canDelete ? (
          <Popconfirm
            title="Delete this tariff record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          style={{ marginTop: 16 }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="tariffCode" label="Tariff Code">
              <Input disabled placeholder="Auto-generated on save" style={{ ...MONO, fontWeight: 700 }} />
            </Form.Item>

            <Form.Item name="powerFacilitationCharge" label="PF Charge (Paise/unit)" rules={[{ required: true, message: 'PF Charge is required' }]}>
              <InputNumber min={0} step={1} precision={0} style={{ width: '100%' }} addonAfter="Paise" placeholder="e.g. 50" />
            </Form.Item>

            <Form.Item name="effectiveFrom" label="Effective From" rules={[{ required: true, message: 'Effective From date is required' }]}>
              <Input type="date" style={MONO} />
            </Form.Item>

            <Form.Item name="effectiveTo" label="Effective To (Optional)">
              <Input type="date" style={MONO} placeholder="Leave blank for ongoing active tariff" />
            </Form.Item>

            <Form.Item name="status" label="Status" initialValue="active">
              <Select options={[{ value: 'active', label: 'Active' }, { value: 'superseded', label: 'Superseded' }]} />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Tariff Description" rules={[{ required: true, message: 'Description is required' }]}>
            <Input placeholder="e.g. Shakthi Power Commercial Facilitation Charge" />
          </Form.Item>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {mode === 'edit' ? 'Update Tariff' : 'Save Tariff'}
            </Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // Helper to fetch tariff history for view modal & drawer
  const getTariffHistory = (tariffCode?: string) => {
    if (!tariffCode) return []
    return tariffs
      .filter(t => t.tariffCode === tariffCode)
      .sort((a, b) => new Date(b.effectiveFrom).getTime() - new Date(a.effectiveFrom).getTime())
  }

  // ── List View ──────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={7} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <PercentageOutlined style={{ color: '#059669' }} /> Shakthi Power Tariff Master
          </h2>
          <p className="page-subtitle">Sakthi Power commercial tariffs — facilitation charges and historical revision tracking</p>
        </div>
        {canAdd && (
          <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add Sakthi Power Tariff</Button>
        )}
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total Tariffs', value: tariffs.length, icon: <PercentageOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Avg PF Charge', value: activePfTariffs.length ? `${(avgPf * 100).toFixed(0)} Paise/unit` : '—', icon: <DashboardOutlined style={{ fontSize: 24, color: '#F59E0B' }} />, iconBg: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' },
          { label: 'Highest PF Charge', value: activePfTariffs.length ? `${(maxPf * 100).toFixed(0)} Paise/unit` : '—', icon: <ArrowUpOutlined style={{ fontSize: 24, color: '#2563EB' }} />, iconBg: 'rgba(37,99,235,0.08)', borderColor: '#2563EB' },
        ]}
      />

      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by code or description..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Superseded', value: 'superseded' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredTariffs}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} tariffs` }}
        />
      </Card>

      {/* ── Revise Tariff Modal ────────────────────────────────────── */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <HistoryOutlined style={{ color: '#D97706', fontSize: 20 }} />
            <span>Revise Tariff Rate — {reviseModalRecord?.tariffCode}</span>
          </div>
        }
        open={!!reviseModalRecord}
        onCancel={() => { setReviseModalRecord(null); reviseForm.resetFields(); }}
        onOk={handleSaveRevision}
        confirmLoading={savingRevision}
        okText="Save Tariff Revision"
        width={540}
      >
        <Form form={reviseForm} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="tariffCode" label="Tariff Code">
            <Input disabled style={{ ...MONO, fontWeight: 700 }} />
          </Form.Item>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="currentPfCharge" label="Current PF Charge">
              <InputNumber disabled style={{ width: '100%', ...MONO }} addonAfter="Paise" />
            </Form.Item>

            <Form.Item name="newPfCharge" label="New Revised PF Charge" rules={[{ required: true, message: 'New PF charge is required' }]}>
              <InputNumber min={0} step={1} precision={0} style={{ width: '100%', ...MONO }} addonAfter="Paise" placeholder="e.g. 60" />
            </Form.Item>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="effectiveFrom" label="New Effective From" rules={[{ required: true, message: 'Effective From is required' }]}>
              <Input type="date" style={MONO} />
            </Form.Item>

            <Form.Item name="effectiveTo" label="Effective To (Optional)">
              <Input type="date" style={MONO} placeholder="Leave blank for ongoing" />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Description">
            <Input placeholder="Tariff Description" />
          </Form.Item>

          <Form.Item name="notes" label="Revision Reason / Notes">
            <Input.TextArea rows={2} placeholder="Reason for revising the facilitation charge" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Detail Drawer with Tariff History ───────────────────────── */}
      <Drawer
        title={
          <div>
            <div style={{ fontWeight: 700, fontSize: 16, color: '#001A4D' }}>{viewRecord?.tariffCode}</div>
            <div style={{ fontSize: 12, color: '#8A93A6' }}>{viewRecord?.description}</div>
          </div>
        }
        open={!!viewRecord}
        onClose={() => setViewRecord(null)}
        width={460}
        extra={
          <Space>
            {canEdit && viewRecord?.status === 'active' && (
              <Button
                type="primary"
                ghost
                icon={<HistoryOutlined />}
                onClick={() => {
                  const rec = viewRecord!
                  setViewRecord(null)
                  openReviseModal(rec)
                }}
              >
                Revise Tariff
              </Button>
            )}
            {canEdit && (
              <Tooltip title="Edit Tariff">
                <Button type="text" icon={<EditOutlined />} onClick={() => { if (viewRecord) goEdit(viewRecord.id); setViewRecord(null) }} />
              </Tooltip>
            )}
            {canDelete && (
              <Popconfirm
                title="Delete this tariff record?"
                description="This action cannot be undone."
                onConfirm={() => { handleDelete(viewRecord!.id); setViewRecord(null) }}
                okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
              >
                <Button type="text" danger icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        }
      >
        {viewRecord && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>
                {viewRecord.status === 'active' ? 'Active Tariff' : 'Superseded Tariff'}
              </span>
            </div>

            <DetailRow label="Tariff Code" value={<span style={MONO}>{viewRecord.tariffCode}</span>} />
            <DetailRow label="Description" value={viewRecord.description} />
            <DetailRow
              label="PF Facilitation Charge"
              value={
                <span style={{ ...MONO, fontWeight: 700, fontSize: 16, color: '#059669' }}>
                  {viewRecord.powerFacilitationCharge != null ? `${(Number(viewRecord.powerFacilitationCharge) * 100).toFixed(0)} Paise/unit` : '—'}
                </span>
              }
            />
            <DetailRow label="Effective From" value={<span style={MONO}>{viewRecord.effectiveFrom}</span>} />
            <DetailRow
              label="Effective To"
              value={viewRecord.effectiveTo ? <span style={MONO}>{viewRecord.effectiveTo}</span> : <Tag color="blue" style={{ borderRadius: 100, fontSize: 11 }}>Current</Tag>}
            />

            {/* Tariff Revision History Timeline */}
            <div style={{ marginTop: 24, paddingTop: 16, borderTop: '1px solid #E4E8F0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <ClockCircleOutlined style={{ color: '#2563EB', fontSize: 16 }} />
                <Text strong style={{ fontSize: 14, color: '#0B1426' }}>
                  Tariff Revision History ({getTariffHistory(viewRecord.tariffCode).length})
                </Text>
              </div>

              <Timeline
                items={getTariffHistory(viewRecord.tariffCode).map((t) => ({
                  color: t.status === 'active' ? 'green' : 'gray',
                  children: (
                    <div style={{ fontSize: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontWeight: 700, color: t.status === 'active' ? '#059669' : '#4A5568', fontSize: 13, ...MONO }}>
                          {(Number(t.powerFacilitationCharge || 0) * 100).toFixed(0)} Paise/unit
                        </span>
                        <Tag color={t.status === 'active' ? 'green' : 'default'} style={{ borderRadius: 6, fontSize: 10 }}>
                          {t.status === 'active' ? 'Active' : 'Superseded'}
                        </Tag>
                      </div>
                      <div style={{ color: '#8A93A6', fontSize: 11, ...MONO }}>
                        Effective: {t.effectiveFrom} {t.effectiveTo ? `to ${t.effectiveTo}` : '(Ongoing)'}
                      </div>
                    </div>
                  ),
                }))}
              />
            </div>
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default TariffMaster
